/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import enUS from '../i18n/en_US.json';
import arSA from '../i18n/ar_SA.json';
import daDK from '../i18n/da_DK.json';
import deDE from '../i18n/de_DE.json';
import enGB from '../i18n/en_GB.json';
import esBO from '../i18n/es_BO.json';
import esES from '../i18n/es_ES.json';
import fiFI from '../i18n/fi_FI.json';
import frCA from '../i18n/fr_CA.json';
import itIT from '../i18n/it_IT.json';
import jaJP from '../i18n/ja_JP.json';
import nlNL from '../i18n/nl_NL.json';
import noNO from '../i18n/no_NO.json';
import ptBR from '../i18n/pt_BR.json';
import ruRU from '../i18n/ru_RU.json';
import svSE from '../i18n/sv_SE.json';
import trTR from '../i18n/tr_TR.json';
import viVN from '../i18n/vi_VN.json';
import zhCN from '../i18n/zh_CN.json';
import zhTW from '../i18n/zh_TW.json';

const locales = {
  en_US: enUS,
  ar_SA: arSA,
  da_DK: daDK,
  de_DE: deDE,
  en_GB: enGB,
  es_BO: esBO,
  es_ES: esES,
  fi_FI: fiFI,
  fr_CA: frCA,
  it_IT: itIT,
  ja_JP: jaJP,
  nl_NL: nlNL,
  no_NO: noNO,
  pt_BR: ptBR,
  ru_RU: ruRU,
  sv_SE: svSE,
  tr_TR: trTR,
  vi_VN: viVN,
  zh_CN: zhCN,
  zh_TW: zhTW
}

const i18nify = (messages, key, ...placeholders) => {
  if (typeof key === 'undefined') {
    return '';
  }

  const message = (typeof key === 'string' && messages && messages[key.trim()]) || String(key);
  const messagePlaceholders = typeof placeholders[0] === 'object' ? placeholders[0] : placeholders;

  return message.replace(/\{([^}]+)\}/g, (match, placeholderKey) => i18nify(messages, messagePlaceholders[placeholderKey.trim()]));
};

const decodeHexMessage = (message) =>
  message.replace(/\\u(\w{4})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)));

const decodeHexMessages = (messages) =>
  Object.assign(
    {},
    ...Object.keys(messages).map((key) => ({
      [key]: decodeHexMessage(messages[key] || ''),
    }))
  );

const createI18NContextValue = (messages = {}) => ({
  ...messages,
  get: (key, ...placeholders) => i18nify(messages, key, ...placeholders)
});

export const DEFAULT_LANGUAGE = 'en_US';

export const getMessages = (lang) => locales[lang] || locales[DEFAULT_LANGUAGE];

export const I18NContext = createContext(createI18NContextValue());

export const I18NConsumer = I18NContext.Consumer;

export const useI18N = () => useContext(I18NContext);

export const getI18N = async (url, callback, signal) => {
  if (!url) {
    return;
  }

  window.I18N_CACHE = window.I18N_CACHE || {};

  if (window.I18N_CACHE[url]) {
    callback(window.I18N_CACHE[url]);
    return;
  }

  let messages = {};

  try {
    const response = await fetch(url, {
      credentials: 'same-origin',
      signal,
      headers: {
        Accept: 'application/json'
      }
    });

    if (signal?.aborted) {
      return;
    }

    const contentType = response.headers.get('content-type') || '';
    messages = contentType.includes('application/json') || contentType.includes('+json')
      ? decodeHexMessages(await response.json())
      : {};
  } catch (error) {
    if (signal?.aborted) {
      return;
    }

    messages = {};
  }

  if (signal?.aborted) {
    return;
  }

  window.I18N_CACHE[url] = messages;
  callback(messages);
};

export const I18NProvider = (props) => {
  const { i18nContext, url, children } = props;
  const [messages, setMessages] = useState(i18nContext || {});

  useEffect(() => {
    if (i18nContext) {
      setMessages(i18nContext);
      return;
    }

    if (!url) {
      return undefined;
    }

    const controller = new AbortController();
    getI18N(url, setMessages, controller.signal);

    return () => {
      controller.abort();
    };
  }, [i18nContext, url]);

  const contextValue = useMemo(() => createI18NContextValue(messages), [messages]);

  return <I18NContext.Provider value={contextValue}>{children}</I18NContext.Provider>;
};

export const BasicI18NProvider = (props) => {
  const { messages, children } = props;

  return <I18NContext.Provider value={createI18NContextValue(messages)}>{children}</I18NContext.Provider>;
};

export const LocalI18NProvider = (props) => {
  const { language = DEFAULT_LANGUAGE, messages, children } = props;
  const defaultMessages = messages[DEFAULT_LANGUAGE] || {};
  const localizedMessages = language === DEFAULT_LANGUAGE ? defaultMessages : {
    ...defaultMessages,
    ...(messages[language] || {})
  };

  return <BasicI18NProvider messages={localizedMessages}>{children}</BasicI18NProvider>;
};

export const I18N = (props) => {
  const { children, as, forwardedRef, i18n: i18nProp, ...customProps } = props;
  const i18n = useI18N();
  const key = typeof children === 'string' ? children : '';
  let message = '';

  if (i18nProp) {
    message = i18n.get(key, i18nProp);
  } else {
    const [_key, ...placeholder] = key.split(/\s*,\s*/);
    message = i18n.get(_key, placeholder);
    message = message === _key ? key : message;
  }

  if (!as && Object.keys(customProps).length === 0) {
    return <>{message ?? children ?? ''}</>;
  }

  const Tag = as || 'span';
  const outputProps = Object.assign(
    {},
    ...Object.keys(customProps).map((propName) => {
      if (!/^i18n-/i.test(propName)) {
        return { [propName]: customProps[propName] };
      }

      const outputPropName = propName.replace(/^i18n-/i, '');

      if (typeof customProps[propName] !== 'string') {
        return { [outputPropName]: message };
      }

      const [_key, ...placeholder] = customProps[propName].split(/\s*,\s*/);
      let propMessage = i18n.get(_key, placeholder);
      propMessage = propMessage === _key ? customProps[propName] : propMessage;

      return { [outputPropName]: propMessage };
    }),
    { children: message || children }
  );

  return <Tag ref={forwardedRef} {...outputProps} />;
};

export default I18N;
